import { useMemo, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import CompletionQueue from "./CompletionQueue";
import { useCoachingDay, localDay } from "./GuidedDay";

const Body = ({ event, onClose }) => {
  const taskId = event.id.replace(/^task:/, "");
  const { data, error, reload } = useCoachingDay(event.date);
  const [review, setReview] = useState(false);
  const focus = useMemo(() => ({ id: taskId, key: `${taskId}:${review}` }), [taskId, review]);
  const task = data?.tasks?.find(t => t.task_id === taskId);
  if (error) return <div role="alert" data-testid="task-log-error" className="text-sm space-y-3"><p>{error}</p><Button data-testid="task-log-retry" onClick={reload}>Try again</Button></div>;
  if (!data) return <p role="status" data-testid="task-log-loading" className="text-sm">Opening this session…</p>;
  if (!task) return <p data-testid="task-log-missing" className="text-sm">This session is no longer in your coaching list.</p>;
  const future = task.date > localDay();
  return <div className="space-y-4">
    <p className="text-sm text-muted-foreground" data-testid="task-log-meta">{task.athlete_name} · {task.date}</p>
    {task.actual && task.can_review && <div className="flex gap-2" role="tablist">
      <Button data-testid="task-log-mode-log" size="sm" variant={review ? "outline" : "default"} onClick={() => setReview(false)}>Actual work</Button>
      <Button data-testid="task-log-mode-review" size="sm" variant={review ? "default" : "outline"} onClick={() => setReview(true)}>{task.review ? "Edit feedback" : "Give feedback"}</Button>
    </div>}
    {future && !review ? <div data-testid="task-log-future" className="text-sm space-y-2"><p>Scheduled for {task.date}. Log what actually happened once the session is done.</p>{task.details && <p className="whitespace-pre-wrap text-muted-foreground">{task.details}</p>}</div>
      : <CompletionQueue key={String(review)} single tasks={[task]} reviewMode={review} focusTask={focus} onSaved={async () => { await reload(); onClose(); }} />}
  </div>;
};

export const TaskLogDialog = ({ event, onClose }) => <Dialog open={!!event} onOpenChange={o => { if (!o) onClose(); }}>
  <DialogContent data-testid="task-log-dialog" className="max-w-3xl w-[96vw] max-h-[90vh] overflow-y-auto">
    <DialogHeader><DialogTitle data-testid="task-log-title">{event?.title || "Session"}</DialogTitle><DialogDescription>Record what this athlete actually did, then review it.</DialogDescription></DialogHeader>
    {event && <Body key={event.id} event={event} onClose={onClose} />}
  </DialogContent>
</Dialog>;
